import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '@/api/client';
import { qk } from '@/api/keys';
import { gates } from '@/app/gates';
import { Banner, Button, Checkbox, Icon, Select, Spinner, toast } from '@/components/ui';
import { LegalText } from '@/components/domain/LegalText';
import { messageOf } from '@/api/errors';
import { useSession } from '@/auth/session';
import { useResolvedTheme } from '@/theme/theme';
import { useMediaQuery } from '@/lib/hooks';
import logoDark from '@/assets/text_logo_dark.png';
import logoLight from '@/assets/text_logo_light.png';
import s from './Gates.module.css';

/** Wire values are `tos`, `privacy_policy` and `disclaimer` (the phone's `account.dart` mapping);
 *  the longer spellings are tolerated too. Anything unknown shows as sent rather than as nothing. */
export function legalDocumentName(documentType: string): string {
  switch (documentType) {
    case 'tos':
    case 'terms_of_service':
      return 'Terms of Service';
    case 'privacy_policy':
      return 'Privacy Policy';
    case 'disclaimer':
    case 'risk_disclaimer':
      return 'Risk Disclaimer';
    default:
      return documentType;
  }
}

function Centered({ children, wide, overlay }: { children: ReactNode; wide?: boolean; overlay?: boolean }) {
  const theme = useResolvedTheme();
  return (
    <div className={overlay ? `${s.screen} ${s.overlay}` : s.screen} role={overlay ? 'dialog' : undefined} aria-modal={overlay || undefined}>
      <div className={wide ? s.cardWide : s.card}>
        <img src={theme === 'light' ? logoLight : logoDark} alt="SixOne8" className={s.logo} />
        {children}
      </div>
    </div>
  );
}

export function Splash() {
  return (
    <div className={s.screen}>
      <Spinner size={24} />
    </div>
  );
}

/** 426: this build is older than the server accepts. On the web the fix is a reload. */
export function UpdateRequired() {
  return (
    <Centered>
      <h1 className={s.title}>A newer version of SixOne8 is available</h1>
      <p className={s.body}>This page is out of date. Reload to get the latest version.</p>
      <Button variant="primary" size="lg" block icon="refresh" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </Centered>
  );
}

/**
 * Below the compact breakpoint the web app points visitors at a computer. The phone apps are not
 * released yet, so no store badges are shown until they are (`StoreBadge` below is kept for that).
 */
export function PhoneGate({ onContinue }: { onContinue: () => void }) {
  return (
    <Centered>
      <Icon name="computer" size={32} className={s.gateIcon} />
      <h1 className={s.title}>Open SixOne8 on your computer</h1>
      <p className={s.body}>
        SixOne8 on the web is laid out for a wide screen. Open app.sixone8.app on a computer and
        everything is here. Apps for iPhone and Android are coming soon.
      </p>
      <button type="button" className={s.continue} onClick={onContinue}>
        Continue anyway
      </button>
    </Centered>
  );
}

/**
 * Over the app when the window is narrower than it is laid out for. A tablet held upright is asked to
 * turn; a phone held sideways (which the phone screen above lets through) is pointed at a computer; a
 * mouse user is asked for a wider window. "Continue anyway" keeps the clipped layout for the session.
 */
export function NarrowGate({ onContinue }: { onContinue: () => void }) {
  const touch = useMediaQuery('(pointer: coarse)');
  const upright = useMediaQuery('(orientation: portrait)');
  const [icon, title, body] = !touch
    ? ['width', 'Make the window wider', 'SixOne8 on the web is laid out for a window at least 1,024 pixels wide. Widen this one and everything is here.']
    : upright
      ? ['screen_rotation', 'Turn your tablet to landscape', 'SixOne8 on the web is laid out for a wide screen. Turn your tablet sideways and everything is here.']
      : ['smartphone', 'This screen is too small for the web app', 'SixOne8 on the web is laid out for a wide screen. On a tablet or a computer, this page is the full web app. Apps for iPhone and Android are coming soon.'];
  return (
    <Centered overlay>
      <Icon name={icon} size={32} className={s.gateIcon} />
      <h1 className={s.title}>{title}</h1>
      <p className={s.body}>{body}</p>
      <button type="button" className={s.continue} onClick={onContinue}>
        Continue anyway
      </button>
    </Centered>
  );
}

/**
 * Over the signed-in app on a phone held upright — almost always someone who signed up from a share
 * link on Instagram, since the share page is the one route the phone screen lets through. The
 * tablet copy above ("turn your tablet") was wrong for them, and "open it on a computer" alone left
 * the strategy they came for behind on a device they will not open it on. So it says the strategy
 * is saved, and sends this page's link to a computer: a draft to their own address in the phone's
 * mail app (no server email needed), or the link copied.
 */
export function PhoneSignedInGate({
  strategyName,
  email,
  onSave,
  saving,
  onContinue,
}: {
  strategyName?: string;
  email?: string;
  /** On a share link not yet saved (an existing account opening one): save it from here. */
  onSave?: () => void;
  saving?: boolean;
  onContinue: () => void;
}) {
  const link = window.location.href;
  const subject = strategyName ? `Open ${strategyName} on SixOne8` : 'Open SixOne8 on my computer';
  const body = `${strategyName ? `${strategyName} is saved in my SixOne8 Strategies, ready to test.\n\n` : ''}Open on a computer: ${link}`;
  const mailto = `mailto:${email ?? ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch {
      toast.error(`Copy this link: ${link}`);
    }
  };
  return (
    <Centered overlay>
      <Icon name="check_circle" size={32} className={s.gateIcon} />
      <h1 className={s.title}>{onSave ? 'Save the strategy you were sent' : strategyName ? `${strategyName} is saved to your account` : "You're signed in"}</h1>
      <p className={s.body}>
        SixOne8 on the web is built for a bigger screen. Open this link on your computer and sign in with the same account —
        {strategyName ? ' your strategy is waiting in Strategies, ready to test.' : ' everything is waiting there.'} Apps for iPhone and
        Android are coming soon.
      </p>
      {onSave && (
        <Button variant="primary" size="lg" block icon="content_copy" loading={saving} onClick={onSave}>
          Save to my strategies
        </Button>
      )}
      <Button variant={onSave ? 'secondary' : 'primary'} size="lg" block icon="mail" onClick={() => window.location.assign(mailto)}>
        Email me the link
      </Button>
      <Button size="lg" block icon="link" onClick={() => void copy()}>
        Copy link
      </Button>
      <button type="button" className={s.continue} onClick={onContinue}>
        Continue anyway
      </button>
    </Centered>
  );
}

export function StoreBadge({ store }: { store: 'apple' | 'google' }) {
  // Store listing URLs are configured when the apps are released; until then the badges explain.
  const label = store === 'apple' ? 'Download on the App Store' : 'Get it on Google Play';
  return (
    <span className={s.badge} title={label}>
      <Icon name={store === 'apple' ? 'phone_iphone' : 'android'} size={20} />
      <span className={s.badgeText}>
        <span className={s.badgeSmall}>{store === 'apple' ? 'Download on the' : 'Get it on'}</span>
        <span className={s.badgeBig}>{store === 'apple' ? 'App Store' : 'Google Play'}</span>
      </span>
    </span>
  );
}

const COUNTRIES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Choose your country' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'SG', label: 'Singapore' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'IN', label: 'India' },
  { value: 'PH', label: 'Philippines' },
  { value: 'PK', label: 'Pakistan' },
];

/** First sign-in only: the account needs a country (requirements §1.12; it gates instruments). */
export function CountryStep() {
  const { completeSignUp, signOut } = useSession();
  const [country, setCountry] = useState('');
  const [emails, setEmails] = useState(true);
  const [busy, setBusy] = useState(false);
  return (
    <Centered>
      <h1 className={s.title}>Where are you based?</h1>
      <p className={s.body}>
        Your country decides which markets SixOne8 can show you. It can only be changed by contacting
        support.
      </p>
      <Select options={COUNTRIES} value={country} onChange={setCountry} aria-label="Country" className={s.select} />
      {/* The refusal PECR's soft opt-in needs at the moment the address is collected
          (`docs/ops/legitimate_interests.md` §4.6): every new account, whatever the sign-in method,
          is created here. The emails' own unsubscribe and Account's switch cover the rest. */}
      <div className={s.emailChoice}>
        <Checkbox checked={emails} onChange={setEmails} label="Email me a couple of tips to help me get started" />
        <span className={s.emailHint}>You can turn them off at any time.</span>
      </div>
      <Button
        variant="primary"
        size="lg"
        block
        disabled={!country}
        loading={busy}
        onClick={async () => {
          setBusy(true);
          await completeSignUp(country, emails);
          setBusy(false);
        }}
      >
        Create account
      </Button>
      <button type="button" className={s.continue} onClick={() => void signOut()}>
        Use a different account
      </button>
    </Centered>
  );
}

/**
 * A new email/password account stops here until its address is verified: the server refuses the
 * account exchange before then (`email_not_verified`), so nothing exists yet but the Firebase user.
 * The phone's verification step (`email_sign_in_screen.dart`), with its copy.
 */
export function VerifyEmailStep() {
  const { confirmEmailVerified, signOut } = useSession();
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState<'confirm' | 'resend' | null>(null);
  const [message, setMessage] = useState<{ tone: 'neg' | 'pos' | 'info'; text: string } | null>(null);
  useEffect(() => {
    void import('@/auth/firebase').then((f) => setEmail(f.currentEmail()));
  }, []);
  const run = async (kind: 'confirm' | 'resend', fn: () => Promise<void>) => {
    setBusy(kind);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ tone: 'neg', text: messageOf(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <Centered>
      <h1 className={s.title}>Check your inbox</h1>
      <p className={s.body}>
        We sent a link to {email ?? 'your email address'}. Open it to confirm the address, then come back here.
      </p>
      {message && <Banner tone={message.tone}>{message.text}</Banner>}
      <Button
        variant="primary"
        size="lg"
        block
        loading={busy === 'confirm'}
        disabled={busy !== null}
        onClick={() =>
          run('confirm', async () => {
            if (!(await confirmEmailVerified())) {
              setMessage({ tone: 'info', text: "That address isn't confirmed yet. Open the link in the email, then try again." });
            }
          })
        }
      >
        I&apos;ve confirmed it
      </Button>
      <Button
        size="lg"
        block
        loading={busy === 'resend'}
        disabled={busy !== null}
        onClick={() =>
          run('resend', async () => {
            await (await import('@/auth/firebase')).resendEmailVerification();
            setMessage({ tone: 'pos', text: 'Sent. It can take a minute to arrive — check spam too.' });
          })
        }
      >
        Send the email again
      </Button>
      <button type="button" className={s.continue} onClick={() => void signOut()}>
        Use a different account
      </button>
    </Centered>
  );
}

/** Blocking: every product route answers 403 until the current documents are accepted. */
export function LegalGate() {
  const qc = useQueryClient();
  const docs = useQuery({ queryKey: qk.legal, queryFn: () => call(api.GET('/api/v1/legal-documents/current')) });
  const outstanding = (docs.data ?? []).filter((d) => d.outstanding);
  const [index, setIndex] = useState(0);
  const accept = useMutation({
    mutationFn: (id: string) => call(api.POST('/api/v1/legal-acceptances', { body: { legal_document_id: id } })),
  });

  if (docs.isLoading) return <Splash />;
  if (docs.error) {
    return (
      <Centered>
        <Banner tone="neg">{messageOf(docs.error)}</Banner>
        <Button onClick={() => void docs.refetch()}>Try again</Button>
      </Centered>
    );
  }
  const current = outstanding[Math.min(index, outstanding.length - 1)];
  if (!current) {
    gates.getState().clearLegal();
    return <Splash />;
  }
  const name = legalDocumentName(current.document.document_type);
  return (
    <Centered wide>
      <h1 className={s.title}>{current.first_acceptance ? `Please review our ${name}` : `We've updated our ${name}`}</h1>
      <p className={s.body}>
        Version {current.document.version}. {outstanding.length > 1 && `Document ${index + 1} of ${outstanding.length}.`}
      </p>
      <div className={s.legalBody}>
        <LegalText content={current.document.content} />
      </div>
      {accept.error && <Banner tone="neg">{messageOf(accept.error)}</Banner>}
      <Button
        variant="primary"
        size="lg"
        block
        loading={accept.isPending}
        onClick={async () => {
          await accept.mutateAsync(current.document.id);
          if (index + 1 < outstanding.length) setIndex(index + 1);
          else {
            gates.getState().clearLegal();
            await qc.invalidateQueries();
          }
        }}
      >
        I agree
      </Button>
    </Centered>
  );
}

export { Centered as GateCard };
